import React from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { ArrowUpward as EscalateIcon } from '@mui/icons-material';

export type EscalationLevel = 'l2' | 'admin';
export type EscalationDuration = 15 | 30 | 120 | 240 | 480;

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
] as const;

interface EscalationRequestFormProps {
  ceiling: string;
  selectedLevel: EscalationLevel;
  selectedDuration: EscalationDuration;
  acting: boolean;
  onLevelChange: (level: EscalationLevel) => void;
  onDurationChange: (duration: EscalationDuration) => void;
  onSubmit: () => void;
}

// The level and duration pickers of an escalation request, with its submit button. The caller
// owns the state and chooses the container: a dialog on a phone, a popover elsewhere.
const EscalationRequestForm: React.FC<EscalationRequestFormProps> = ({
  ceiling,
  selectedLevel,
  selectedDuration,
  acting,
  onLevelChange,
  onDurationChange,
  onSubmit,
}) => (
  <>
    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <InputLabel>Level</InputLabel>
      <Select
        value={selectedLevel}
        label="Level"
        onChange={(e) => onLevelChange(e.target.value as EscalationLevel)}
      >
        {(ceiling === 'l2' || ceiling === 'admin') && <MenuItem value="l2">L2</MenuItem>}
        {ceiling === 'admin' && <MenuItem value="admin">Admin</MenuItem>}
      </Select>
    </FormControl>

    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <InputLabel>Duration</InputLabel>
      <Select
        value={selectedDuration}
        label="Duration"
        onChange={(e) => onDurationChange(Number(e.target.value) as EscalationDuration)}
      >
        {DURATION_OPTIONS.map(d => (
          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
        ))}
      </Select>
    </FormControl>

    <Button
      fullWidth
      variant="contained"
      onClick={onSubmit}
      disabled={acting}
      startIcon={acting ? <CircularProgress size={14} /> : <EscalateIcon />}
    >
      {selectedLevel === 'admin' ? 'Request Admin' : 'Escalate to L2'}
    </Button>

    {selectedLevel === 'admin' && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Admin escalation requires approval from another admin.
      </Typography>
    )}
  </>
);

export default EscalationRequestForm;
